/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import pluginFactory from 'taoTests/runner/plugin';
import { __ } from '@oat-sa-private/ui-core';
import { mount, unmount } from 'svelte';
import ImageLightbox from './ImageLightbox.svelte';

const pluginName = 'imageZoom';
const triggerClass = 'image-zoom-trigger';
const excludedAncestorSelector =
    'a, button, input, select, textarea, label, [contenteditable="true"], [role="button"], [role="link"]';
const enhancedAttributes = ['role', 'tabindex', 'aria-label'];

/**
 * Adds an accessible lightbox to passive item-content images.
 */
export default pluginFactory({
    name: pluginName,

    install() {
        this.component = null;
        this.contentArea = null;
        this.enhancedImages = new Map();
        this.triggerImage = null;
        this.backgroundState = null;

        this.isEligibleImage = image => {
            if (
                !(image instanceof HTMLImageElement) ||
                !image.closest('.qti-item') ||
                image.closest(excludedAncestorSelector)
            ) {
                return false;
            }

            const interaction = image.closest('.qti-interaction');
            const prompt = image.closest('.qti-prompt');
            return !interaction || prompt?.closest('.qti-interaction') === interaction;
        };

        this.enhanceImage = image => {
            if (!this.isEligibleImage(image) || this.enhancedImages.has(image)) {
                return;
            }

            this.enhancedImages.set(image, {
                attributes: Object.fromEntries(
                    enhancedAttributes.map(attribute => [attribute, image.getAttribute(attribute)])
                ),
                cursor: image.style.cursor,
                hadTriggerClass: image.classList.contains(triggerClass)
            });
            image.classList.add(triggerClass);
            image.style.cursor = 'pointer';
            image.setAttribute('role', 'button');
            image.setAttribute('tabindex', '0');
            const label = __('Open image preview');
            image.setAttribute('aria-label', image.alt ? `${label}: ${image.alt}` : label);
        };

        this.enhanceImages = root => {
            if (root instanceof HTMLImageElement) {
                this.enhanceImage(root);
            }
            root?.querySelectorAll?.('img').forEach(this.enhanceImage);
        };

        this.restoreImages = () => {
            this.enhancedImages.forEach(({ attributes, cursor, hadTriggerClass }, image) => {
                if (!hadTriggerClass) {
                    image.classList.remove(triggerClass);
                }
                enhancedAttributes.forEach(attribute => {
                    const value = attributes[attribute];
                    if (value === null || typeof value === 'undefined') {
                        image.removeAttribute(attribute);
                    } else {
                        image.setAttribute(attribute, value);
                    }
                });
                image.style.cursor = cursor || '';
            });
            this.enhancedImages.clear();
        };

        this.hideBackground = () => {
            const background = this.getAreaBroker().getTestRunnerArea();
            if (!background) {
                return;
            }
            this.backgroundState = {
                element: background,
                inert: background.hasAttribute('inert'),
                ariaHidden: background.getAttribute('aria-hidden')
            };
            background.setAttribute('inert', '');
            background.setAttribute('aria-hidden', 'true');
        };

        this.restoreBackground = () => {
            if (!this.backgroundState) {
                return;
            }
            const { element, inert, ariaHidden } = this.backgroundState;
            if (!inert) {
                element.removeAttribute('inert');
            }
            if (ariaHidden === null) {
                element.removeAttribute('aria-hidden');
            } else {
                element.setAttribute('aria-hidden', ariaHidden);
            }
            this.backgroundState = null;
        };

        this.close = () => {
            if (this.component) {
                unmount(this.component);
                this.component = null;
            }
            this.restoreBackground();
            if (this.triggerImage?.isConnected) {
                this.triggerImage.focus();
            }
            this.triggerImage = null;
        };

        this.open = image => {
            this.close();
            this.triggerImage = image;
            this.hideBackground();
            this.component = mount(ImageLightbox, {
                target: this.getAreaBroker().getContainer(),
                props: {
                    src: image.currentSrc || image.src,
                    alt: image.alt || '',
                    onClose: this.close
                }
            });
        };

        this.handleClick = event => {
            const image = event.target.closest?.(`img.${triggerClass}`);
            if (image && this.contentArea?.contains(image)) {
                event.preventDefault();
                event.stopPropagation();
                this.open(image);
            }
        };

        this.handleKeyDown = event => {
            const image = event.target.closest?.(`img.${triggerClass}`);
            if (
                image &&
                this.contentArea?.contains(image) &&
                (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')
            ) {
                event.preventDefault();
                event.stopPropagation();
                this.open(image);
            }
        };

        this.unbindContent = () => {
            if (this.contentArea) {
                this.contentArea.removeEventListener('click', this.handleClick);
                this.contentArea.removeEventListener('keydown', this.handleKeyDown);
            }
            this.contentArea = null;
            this.restoreImages();
        };

        this.bindContent = () => {
            this.unbindContent();
            this.contentArea = this.getAreaBroker().getContentArea();
            if (!this.contentArea) {
                return;
            }

            this.enhanceImages(this.contentArea);
            this.contentArea.addEventListener('click', this.handleClick);
            this.contentArea.addEventListener('keydown', this.handleKeyDown);
        };
    },

    init() {
        this.getTestRunner()
            .on(`renderitem.${pluginName}`, this.bindContent)
            .on(`unloaditem.${pluginName} itemModalFeedback.${pluginName}`, () => {
                this.close();
                this.unbindContent();
            });
    },

    destroy() {
        this.close();
        this.unbindContent();
        this.getTestRunner().off(`.${pluginName}`);
    }
});
